import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { format } from 'date-fns';
import { first, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { Customer } from '../../models';
import { Order } from '../../models/order';
import { OrderRow } from '../../models/orderRow';
import { CustomerService } from '../customers/customer.service';
import { GungStringConverterService } from '../gung-string-converter.service';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  constructor(
    protected http: HttpClient,
    protected customerService: CustomerService
  ) {}

  getOrders(
    customerId: string,
    nocache?: boolean,
    maxSize?: number,
    includeRows?: boolean,
    daysBack?: number
  ): Observable<Order[]> {
    let url = `json/orders/list/${GungStringConverterService.toGungString(customerId)}`;
    if (!!maxSize && !!includeRows) {
      url = `${url}?maxSize=${maxSize}&includeRows=1`;
    } else if (!!maxSize) {
      url = `${url}?maxSize=${maxSize}`;
    } else if (!!includeRows) {
      url = `${url}?includeRows=1`;
    }

    if (daysBack) {
      if (!url.includes('?')) {
        url += '?';
      } else {
        url += '&';
      }
      url += `daysBack=${daysBack}`;
    }

    const headers = {
      maxAge: nocache ? '-1' : '300'
    };
    return this.http.get<Order[]>(url, { headers }).pipe(
      switchMap(orders => {
        if (orders.length === 0) {
          return of([]);
        }

        return this.customerService.getCustomer(customerId).pipe(
          first(),
          map(customer => orders.map(order => this.orderMapper(order, customer)))
        );
      })
    );
  }

  getOrder(orderId: string, nocache?: boolean): Observable<Order> {
    const url = `json/orders/${orderId}`;
    const headers = {
      maxAge: nocache ? '-1' : '300'
    };
    return this.http.get<Order>(url, { headers }).pipe(
      switchMap(order =>
        forkJoin([of(order), this.customerService.getCustomer(order.deliveryCustomerId).pipe(first())])
      ),
      map(([order, customer]) => this.orderMapper(order, customer))
    );
  }

  getOpenOrders(nocache?: boolean, maxSize?: number, fromDate?: Date, toDate?: Date): Observable<Order[]> {
    let url = `json/open-orders`;
    let params: HttpParams;
    if (!!maxSize) {
      url += `?maxSize=${maxSize}`;
    }
    const headers = {
      maxAge: nocache ? '-1' : '60'
    };
    if (fromDate && toDate) {
      params = new HttpParams()
        .set('fromDate', format(fromDate, 'yyyy-MM-dd'))
        .set('toDate', format(toDate, 'yyyy-MM-dd'));
    }
    return this.http.get<Order[]>(url, { headers, params }).pipe(
      switchMap(orders => {
        if (orders.length === 0) {
          return of([]);
        }

        return this.customerService.getCustomers().pipe(
          first(),
          map(customers => this.mapOrdersWithDeliveryCustomers(orders, customers))
        );
      })
    );
  }

  getOpenOrderRows(nocache?: boolean): Observable<any[]> {
    let url = `json/open-order-rows`;
    const headers = { headers: { maxAge: nocache ? '-1' : '60' } };
    return this.http.get<any[]>(url, headers);
  }

  getQuotes(nocache?: boolean): Observable<Order[]> {
    const url = `json/quotes`;
    const headers = {
      maxAge: nocache ? '-1' : '60'
    };
    return this.http.get<Order[]>(url, { headers }).pipe(
      switchMap(orders => {
        if (orders.length === 0) {
          return of([]);
        }

        return this.customerService.getCustomers().pipe(
          first(),
          map(customers => this.mapOrdersWithDeliveryCustomers(orders, customers))
        );
      })
    );
  }

  deleteOrder(orderId: string): Observable<void> {
    const url = `json/orders/${orderId}`;
    return this.http.delete<void>(url);
  }

  updateOrderRow(orderRow: OrderRow) {
    const url = 'json/order-rows';
    return this.http.put<OrderRow>(url, orderRow);
  }

  protected mapOrdersWithDeliveryCustomers(orders: Order[], customers: Customer[]): Order[] {
    const customerById = new Map(customers.map(customer => [customer.id, customer]));

    return orders.map(order => {
      const deliveryCustomerId = GungStringConverterService.fromGungString(order.deliveryCustomerId);
      return this.orderMapper(order, customerById.get(deliveryCustomerId));
    });
  }

  protected orderMapper(order: Order, customer: Customer | undefined): Order {
    if (!order.extra) {
      order.extra = {};
    }
    order.extra.deliveryCustomer = customer;
    return order;
  }

  sendConfrimation(orderId: string, emailRecipients: string[]) {
    return this.http.post('json/order/send-confirmation', { emailRecipients, orderId });
  }

  markAsReleased(orderId: string): Observable<any> {
    return of(new Error('Function not implemented'));
  }

  markAsOpen(orderId: string): Observable<any> {
    return of(new Error('Function not implemented'));
  }
}
